import React, { useEffect, useState } from 'react';
import { Button, Radio, Spin } from 'antd';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useSessionFormState } from '@hooks/useSessionFormState';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useMutation } from '@tanstack/react-query';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { EmbeddedCodingTool, type ICD11Entity } from '@components/EmbeddedCodingTool';
import useClinicalNotesMeta from '@hooks/useClinicalNotesMeta';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { useDebouncedCallback } from '@mantine/hooks';
import { DeleteOutlined } from 'antd/icons';
import { SessionFormItem } from './SessionFormItem';
import { Form } from 'ALIASED-antd';
import { useAppointmentLetterReview } from '../hooks/useAppointmentLetterReview';

const convertStringBooleanToBoolean = (val?: string | null): boolean | null => {
  if (val === 'true') return true;
  if (val === 'false') return false;
  return null;
};

const DiagnosisContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 4px;
  margin-bottom: 10px;

  .diagnosis-list {
    padding: 0;
    margin: 0;
  }

  .diagnosis {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    margin-top: 7px;

    span.text {
      margin-right: 20px;
    }
  }
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;

  .ctw-window {
    position: absolute;
    top: 40%;
    left: 0;
    width: 100%;
    max-height: 10vh;
  }

  .searchresults {
    max-height: 30vh;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  animation: 1.5s linear infinite;

  @keyframes rotate {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Diagnosis = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const {
    appointment: { appointment_completed, read_only, clinician_user_guid }
  } = useSessionAppointment();
  const appointment_id = useSessionAppointmentId();
  const disabled = appointment_completed || read_only;
  const { detailsCurrentAppointment, refetch } = useAppointmentDetails(appointment_id);

  const [icd10Value, setIcd10Value] = useSessionFormState('icd10Value');

  const [diagnosisType, setDiagnosisType] = useState<boolean | null>(
    convertStringBooleanToBoolean(detailsCurrentAppointment?.formal_diagnosis)
  );

  const [isMutating, setIsMutating] = useState(false);

  const form = Form.useFormInstance();
  const invalidateLetterReview = useAppointmentLetterReview(form);

  useEffect(() => {
    setDiagnosisType(convertStringBooleanToBoolean(detailsCurrentAppointment?.formal_diagnosis));
  }, [detailsCurrentAppointment?.formal_diagnosis]);

  const debouncedRefetch = useDebouncedCallback(async () => {
    await refetch();
    setIsMutating(false);
  }, 500);

  const { mutate: handleSaveDiagnosisType } = useMutation({
    mutationFn: ({ formal_diagnosis }: { formal_diagnosis: boolean }) =>
      dispatch(
        actions.clinicianDiagnosisTypeSave({
          appt_id: appointment_id,
          user_guid: clinician_user_guid,
          formal_diagnosis
        })
      ),
    onMutate: () => {
      setIsMutating(true);
      invalidateLetterReview({ icd10Value: true });
    },
    onSuccess: async () => {
      debouncedRefetch();
    }
  });

  const { mutate: onSelectICD10 } = useMutation({
    mutationFn: ({ code, title }: Pick<ICD11Entity, 'code' | 'title'>) =>
      dispatch(
        actions.clinicianICD10DiagnosisSave({
          user_guid: clinician_user_guid,
          appt_id: appointment_id,
          icd_code: code,
          icd_description: title,
          icd_version: 'ICD 11'
        })
      ),
    onMutate: () => {
      setIsMutating(true);
      invalidateLetterReview({ icd10Value: true });
    },
    onSuccess: async () => {
      debouncedRefetch();
      setIcd10Value('');
    }
  });

  const { mutate: handleDelete } = useMutation({
    mutationFn: (icd_code: string) =>
      dispatch(actions.clinicianICD10DiagnosisDelete(appointment_id, clinician_user_guid, icd_code)),
    onMutate: () => {
      setIsMutating(true);
      invalidateLetterReview({ icd10Value: true });
    },
    onSuccess: async () => {
      debouncedRefetch();
      setIcd10Value('');
    }
  });

  const { getIsFieldRequired, getNoteLabel } = useClinicalNotesMeta();
  const isRequired = getIsFieldRequired('DIAGNOSIS');
  const hasExistingDiagnoses = () => {
    if (!detailsCurrentAppointment?.icd) return;
    return detailsCurrentAppointment?.icd?.[0]?.icd_code !== 'N/A' && detailsCurrentAppointment?.icd?.length > 0;
  };

  const setDiagnosisToNotApplicable = () =>
    onSelectICD10(
      {
        code: 'N/A',
        title: 'Diagnosis uncertain'
      },
      {
        onSuccess: async () => {
          handleSaveDiagnosisType({
            formal_diagnosis: false
          });
          debouncedRefetch();
        }
      }
    );

  return (
    <SessionFormItem label={getNoteLabel('DIAGNOSIS')} required={isRequired}>
      <InputContainer>
        <EmbeddedCodingTool onSelect={onSelectICD10} onChange={setIcd10Value} value={icd10Value} disabled={disabled} />

        <Button
          data-testid="diagnosis-not-applicable-button"
          disabled={isMutating || detailsCurrentAppointment?.icd?.[0]?.icd_code === 'N/A'}
          onClick={setDiagnosisToNotApplicable}
          aria-label={
            hasExistingDiagnoses()
              ? t(
                  'Set Diagnosis to N/A - Diagnosis Uncertain. Warning: This will remove other diagnoses from the list.'
                )
              : t('Set Diagnosis to N/A - Diagnosis Uncertain.')
          }
        >
          {t('N/A')}
        </Button>
      </InputContainer>

      <DiagnosisContainer>
        {isMutating ? (
          <LoadingContainer>
            <Spin />
          </LoadingContainer>
        ) : (
          <ul className="diagnosis-list">
            {detailsCurrentAppointment?.icd?.map(({ icd_code, icd_desc }) => (
              <li className="diagnosis" key={icd_code}>
                <span className="text">{`${icd_code} - ${icd_desc}`}</span>
                {!disabled && (
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined aria-hidden />}
                    data-testid={`remove-diagnosis-button-${icd_code}`}
                    onClick={() => handleDelete(icd_code)}
                    aria-label={`${t('Remove diagnosis')} ${icd_desc}`}
                  >
                    {t('Remove')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </DiagnosisContainer>

      <Radio.Group
        {...{ role: 'radiogroup', 'aria-label': 'Diagnosis: Type' }}
        value={diagnosisType}
        onChange={(e) => {
          handleSaveDiagnosisType({
            formal_diagnosis: e.target.value
          });
        }}
        css={{ marginTop: '0.5rem' }}
        disabled={isMutating || disabled || detailsCurrentAppointment?.icd?.[0]?.icd_code === 'N/A'}
      >
        <Radio value={false} data-testid="working">
          {t('This is a working diagnosis')}
        </Radio>
        <Radio value={true} data-testid="formal">
          {t('This is a formal diagnosis')}
        </Radio>
      </Radio.Group>
    </SessionFormItem>
  );
};

export default Diagnosis;
